import React, { useState, useEffect, useCallback } from 'react';
import { Zap, Plus, Loader2, Pencil, Trash2, MapPin } from 'lucide-react';
import ElectricalService from '../../services/ElectricalService';
import { Card, Button, Select, toast, ConfirmationModal, ErrorState } from './V2Components';
import PanelBoard from './Electrical/PanelBoard';
import PanelForm from './Electrical/PanelForm';
import CircuitForm from './Electrical/CircuitForm';

/**
 * House "Electrical" tab. Lazy-fetches the house's breaker panels and renders
 * the selected panel as a two-column PanelBoard. Panels and circuits are
 * created/edited through inline in-page forms (no overlay, no drill-in routes).
 */
const ElectricalTab = ({ houseId }) => {
    const [panels, setPanels] = useState([]);
    // `loading` gates the initial full-tab spinner (nothing to show yet).
    // `refreshing` covers every subsequent refetch (after save/delete) so the
    // already-rendered board stays mounted instead of flashing to a spinner —
    // and so a refetch failure surfaces via toast without hiding the current
    // (still-valid) data underneath it.
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    // Set when a fetch fails. Without it a failed initial load falls through
    // to the "No panels tracked yet" empty state — a false empty with no way
    // to retry once the toast fades (same rationale as HouseDetails'
    // utilitiesError).
    const [loadError, setLoadError] = useState(null);
    const [selectedPanelId, setSelectedPanelId] = useState(null);

    // Panel form state. `panelFieldErrors` holds the structured `errors`
    // array from a 422 so the form can surface field-level validation inline
    // instead of collapsing it to a single toast.
    const [showPanelForm, setShowPanelForm] = useState(false);
    const [editingPanel, setEditingPanel] = useState(null);
    const [panelSaving, setPanelSaving] = useState(false);
    const [panelFieldErrors, setPanelFieldErrors] = useState([]);

    // Circuit form state (same field-error handling as panels).
    const [showCircuitForm, setShowCircuitForm] = useState(false);
    const [editingCircuit, setEditingCircuit] = useState(null);
    // Slot number to pre-fill when the user opens the form by clicking a blank
    // slot on the board; null for the plain "Add Circuit" button.
    const [defaultCircuitPosition, setDefaultCircuitPosition] = useState(null);
    const [circuitSaving, setCircuitSaving] = useState(false);
    const [circuitFieldErrors, setCircuitFieldErrors] = useState([]);

    // Delete-confirmation state. Both panel and circuit deletes go through a
    // ConfirmationModal (warning + Cancel/Delete + loading) — the same pattern,
    // now that neither form is an overlay that a second dialog could stack over.
    const [panelToDelete, setPanelToDelete] = useState(null);
    const [deletingPanel, setDeletingPanel] = useState(false);
    const [circuitToDelete, setCircuitToDelete] = useState(null);
    const [deletingCircuit, setDeletingCircuit] = useState(false);

    const fetchPanels = useCallback(async ({ isInitial = false } = {}) => {
        try {
            if (isInitial) setLoading(true);
            else setRefreshing(true);
            const data = await ElectricalService.getPanels(houseId);
            const list = data.panels || [];
            setPanels(list);
            setLoadError(null);
            // Keep the current selection if it still exists; else pick the first.
            setSelectedPanelId((prev) => {
                if (prev && list.some((p) => p.id === prev)) return prev;
                return list.length > 0 ? list[0].id : null;
            });
        } catch (error) {
            setLoadError(error.message || 'Failed to load panels');
            toast.error?.(error.message || 'Failed to load panels');
        } finally {
            if (isInitial) setLoading(false);
            else setRefreshing(false);
        }
    }, [houseId]);

    useEffect(() => {
        if (houseId) fetchPanels({ isInitial: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [houseId]);

    const selectedPanel = panels.find((p) => p.id === selectedPanelId) || null;

    // While either inline form is open, suppress every competing affordance
    // (the other Add trigger, the panel selector, the panel edit/delete icons,
    // and circuit-cell editing). The Sheet's scrim used to block these; inline
    // forms don't, so without this you could open both forms at once, silently
    // replace an in-progress draft, or change the selected panel mid-form and
    // land a new circuit under the wrong panel.
    const formOpen = showPanelForm || showCircuitForm;

    // ------------------------------ Panel handlers -----------------------------

    const handleSavePanel = async (payload) => {
        setPanelSaving(true);
        setPanelFieldErrors([]);
        try {
            if (editingPanel) {
                await ElectricalService.updatePanel(editingPanel.id, payload);
                toast.success('Panel updated');
            } else {
                const created = await ElectricalService.createPanel(houseId, payload);
                toast.success('Panel added');
                if (created?.id) setSelectedPanelId(created.id);
            }
            setShowPanelForm(false);
            setEditingPanel(null);
            await fetchPanels();
        } catch (error) {
            // Keep the form open and show field-level errors inline when the
            // server returned a structured 422; otherwise fall back to a toast.
            if (error.errors?.length) setPanelFieldErrors(error.errors);
            else toast.error?.(error.message || 'Failed to save panel');
        } finally {
            setPanelSaving(false);
        }
    };

    const handleDeletePanel = async () => {
        if (!panelToDelete) return;
        setDeletingPanel(true);
        try {
            await ElectricalService.deletePanel(panelToDelete.id);
            toast.success('Panel deleted');
            setPanelToDelete(null);
            await fetchPanels();
        } catch (error) {
            toast.error?.(error.message || 'Failed to delete panel');
        } finally {
            setDeletingPanel(false);
        }
    };

    // ----------------------------- Circuit handlers ----------------------------

    const handleSaveCircuit = async (payload) => {
        if (!selectedPanel) return;
        setCircuitSaving(true);
        setCircuitFieldErrors([]);
        try {
            if (editingCircuit) {
                await ElectricalService.updateCircuit(editingCircuit.id, payload);
                toast.success('Circuit updated');
            } else {
                await ElectricalService.createCircuit(selectedPanel.id, payload);
                toast.success('Circuit added');
            }
            setShowCircuitForm(false);
            setEditingCircuit(null);
            await fetchPanels();
        } catch (error) {
            // Same inline-422 handling as panels: keep the form open and show
            // field errors when present, else toast.
            if (error.errors?.length) setCircuitFieldErrors(error.errors);
            else toast.error?.(error.message || 'Failed to save circuit');
        } finally {
            setCircuitSaving(false);
        }
    };

    const handleDeleteCircuit = async () => {
        if (!circuitToDelete) return;
        setDeletingCircuit(true);
        try {
            await ElectricalService.deleteCircuit(circuitToDelete.id);
            toast.success('Circuit deleted');
            setCircuitToDelete(null);
            setShowCircuitForm(false);
            setEditingCircuit(null);
            // Clear any lingering 422 errors so they don't leak into the next
            // CircuitForm open — the delete path closes the form without
            // going through onClose (which also resets this).
            setCircuitFieldErrors([]);
            await fetchPanels();
        } catch (error) {
            toast.error?.(error.message || 'Failed to delete circuit');
        } finally {
            setDeletingCircuit(false);
        }
    };

    // --------------------------------- Render ----------------------------------

    if (loading) {
        return (
            <div className="flex justify-center p-12">
                <Loader2 className="animate-spin text-[var(--primary)]" size={32} />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-bold text-[var(--text-main)] flex items-center gap-2">
                        Electrical
                        {refreshing && (
                            <Loader2
                                className="animate-spin text-[var(--text-muted)]"
                                size={14}
                                aria-label="Refreshing"
                            />
                        )}
                    </h3>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                        Map your breaker panels and the circuits inside them.
                    </p>
                </div>
                {panels.length > 0 && !formOpen && (
                    <Button
                        leadingIcon={Plus}
                        onClick={() => {
                            setEditingPanel(null);
                            setShowPanelForm(true);
                        }}
                    >
                        Add Panel
                    </Button>
                )}
            </div>

            {/* Inline Add/Edit Panel form — renders in the page flow below the
                header (like the Add Utility form), no overlay. */}
            <PanelForm
                isOpen={showPanelForm}
                onClose={() => {
                    setShowPanelForm(false);
                    setEditingPanel(null);
                    setPanelFieldErrors([]);
                }}
                onSave={handleSavePanel}
                panel={editingPanel}
                saving={panelSaving}
                fieldErrors={panelFieldErrors}
            />

            {loadError && panels.length === 0 ? (
                // Failed load with nothing cached: a retryable error, NOT the
                // empty state — "No panels tracked yet" would be a lie here.
                <Card>
                    <ErrorState
                        title="Couldn't load panels"
                        message={loadError}
                        onRetry={() => fetchPanels({ isInitial: true })}
                    />
                </Card>
            ) : panels.length === 0 ? (
                // Suppress the empty state while the inline add form is open, so the
                // form (rendered above) is the only surface — mirrors the Utilities tab.
                !showPanelForm && (
                    <Card className="p-10 text-center">
                        <Zap size={48} className="mx-auto mb-3 text-[var(--text-muted)] opacity-50" />
                        <p className="text-[var(--text-muted)] mb-4">No panels tracked yet.</p>
                        <Button
                            leadingIcon={Plus}
                            onClick={() => {
                                setEditingPanel(null);
                                setShowPanelForm(true);
                            }}
                        >
                            Add your first panel
                        </Button>
                    </Card>
                )
            ) : (
                <>
                    {/* Panel selector — only shown when a house has more than one
                        panel, and hidden while a form is open so the selection
                        can't change out from under an in-progress circuit. */}
                    {panels.length > 1 && !formOpen && (
                        <div className="max-w-xs">
                            <Select
                                value={selectedPanelId ?? ''}
                                onChange={(e) => setSelectedPanelId(Number(e.target.value))}
                                aria-label="Select panel"
                            >
                                {panels.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.label}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    )}

                    {selectedPanel && (
                        <Card className="p-3 sm:p-5">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h4 className="text-base font-bold text-[var(--text-main)]">
                                        {selectedPanel.label}
                                    </h4>
                                    <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-[var(--text-secondary)]">
                                        {selectedPanel.location && (
                                            <span className="inline-flex items-center gap-1">
                                                <MapPin size={12} /> {selectedPanel.location}
                                            </span>
                                        )}
                                        {selectedPanel.main_breaker_amps != null && (
                                            <span>{selectedPanel.main_breaker_amps}A main</span>
                                        )}
                                        {selectedPanel.total_slots != null && (
                                            <span>{selectedPanel.total_slots} slots</span>
                                        )}
                                    </div>
                                </div>
                                {!formOpen && (
                                    <div className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            aria-label="Edit panel"
                                            onClick={() => {
                                                setEditingPanel(selectedPanel);
                                                setShowPanelForm(true);
                                            }}
                                            className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)] transition"
                                        >
                                            <Pencil size={16} />
                                        </button>
                                        <button
                                            type="button"
                                            aria-label="Delete panel"
                                            onClick={() => setPanelToDelete(selectedPanel)}
                                            className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--error)] hover:bg-[var(--bg-hover)] transition"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {!formOpen && (
                                <div className="flex justify-end mb-4">
                                    <Button
                                        size="sm"
                                        variant="secondary"
                                        leadingIcon={Plus}
                                        onClick={() => {
                                            setEditingCircuit(null);
                                            setDefaultCircuitPosition(null);
                                            setShowCircuitForm(true);
                                        }}
                                    >
                                        Add Circuit
                                    </Button>
                                </div>
                            )}

                            {/* Inline Add/Edit Circuit form — renders inside the
                                panel card, above the board, no overlay. */}
                            <CircuitForm
                                isOpen={showCircuitForm}
                                onClose={() => {
                                    setShowCircuitForm(false);
                                    setEditingCircuit(null);
                                    setDefaultCircuitPosition(null);
                                    setCircuitFieldErrors([]);
                                }}
                                onSave={handleSaveCircuit}
                                onDelete={() => setCircuitToDelete(editingCircuit)}
                                circuit={editingCircuit}
                                defaultPosition={defaultCircuitPosition}
                                saving={circuitSaving}
                                fieldErrors={circuitFieldErrors}
                            />

                            <PanelBoard
                                circuits={selectedPanel.circuits || []}
                                totalSlots={selectedPanel.total_slots ?? null}
                                readOnly={formOpen}
                                onEditCircuit={(circuit) => {
                                    setEditingCircuit(circuit);
                                    setDefaultCircuitPosition(null);
                                    setShowCircuitForm(true);
                                }}
                                onAddAtPosition={(position) => {
                                    setEditingCircuit(null);
                                    setDefaultCircuitPosition(position);
                                    setShowCircuitForm(true);
                                }}
                            />
                        </Card>
                    )}
                </>
            )}

            <ConfirmationModal
                isOpen={!!panelToDelete}
                onClose={() => setPanelToDelete(null)}
                onConfirm={handleDeletePanel}
                title="Delete panel?"
                message={
                    panelToDelete
                        ? `"${panelToDelete.label}" and all its circuits will be permanently deleted.`
                        : ''
                }
                confirmText="Delete"
                isDanger
                isLoading={deletingPanel}
            />

            <ConfirmationModal
                isOpen={!!circuitToDelete}
                onClose={() => setCircuitToDelete(null)}
                onConfirm={handleDeleteCircuit}
                title="Delete circuit?"
                message={
                    circuitToDelete
                        ? `"${circuitToDelete.label}" will be permanently deleted.`
                        : ''
                }
                confirmText="Delete"
                isDanger
                isLoading={deletingCircuit}
            />
        </div>
    );
};

export default ElectricalTab;
